import { Filter, useSetFilters } from '@ais-ui/websockets';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import {
  FilterCriteria,
  HumanOperator,
  humanOperatorToOperator,
} from '../Filters/constants';
import { useReplayContext } from '../Replay/ReplayContext';
import { useToast } from '@ais-ui/ui-core';
import { useFilterOptions } from '../Filters/useFilterOptions';

export interface SimpleFilter {
  id: string;
  name: string;
  filterType: HumanOperator;
  definition: string;
}

interface FilterContextProps {
  simpleFilters: SimpleFilter[];
  setSimpleFilters: (filterContext: SimpleFilter[]) => void;
  customFilters: Filter[];
  setCustomFilters: (filterContext: Filter[]) => void;
  applyFilter: (showToast: boolean) => Promise<boolean>;
  filterOptions: Record<string, FilterCriteria>;
}

const FilterContext = createContext<FilterContextProps | undefined>(undefined);

function getDefinitionFromSimpleFilter(simpleFilter: SimpleFilter): string {
  const operator = humanOperatorToOperator(simpleFilter.filterType);
  const needsQuotes = simpleFilter.name === 'SHIPLIST';

  switch (simpleFilter.filterType.toLowerCase()) {
    case HumanOperator.Equal:
    case HumanOperator.NotEqual:
    case HumanOperator.LessThan:
    case HumanOperator.LessThanOrEqual:
    case HumanOperator.GreaterThan:
    case HumanOperator.GreaterThanOrEqual:
    case HumanOperator.InsideArea:
    case HumanOperator.OutsideArea:
    case HumanOperator.InsideCircle:
    case HumanOperator.OutsideCircle:
      return needsQuotes
        ? `(${simpleFilter.name} ${operator} "${simpleFilter.definition}")`
        : `(${simpleFilter.name} ${operator} ${simpleFilter.definition})`;
    case HumanOperator.Contains:
    case HumanOperator.NotContains:
      return `(${simpleFilter.name} ${operator} "*${simpleFilter.definition}*")`;
    case HumanOperator.StartsWith:
    case HumanOperator.DoesNotStartWith:
      return `(${simpleFilter.name} ${operator} "${simpleFilter.definition}*")`;
    case HumanOperator.EndsWith:
    case HumanOperator.DoesNotEndWith:
      return `(${simpleFilter.name} ${operator} "*${simpleFilter.definition}")`;
    default:
      throw new Error(`Unsupported filterType: ${simpleFilter.filterType}`);
  }
}

function buildSimpleFilterDefinition(
  definition: string,
  simpleFilters: SimpleFilter[]
) {
  simpleFilters.forEach((simpleFilter) => {
    const filterDefinition = getDefinitionFromSimpleFilter(simpleFilter);
    if (!definition) {
      definition += `(${filterDefinition})`;
    } else {
      definition += ` AND (${filterDefinition})`;
    }
  });
  return definition;
}

function buildCustomFilterDefinition(
  definition: string,
  customFilters: Filter[]
) {
  customFilters.forEach((customFilter) => {
    const filterDefinition = customFilter.filter;
    if (!definition) {
      definition += `(${filterDefinition})`;
    } else {
      definition += ` AND (${filterDefinition})`;
    }
  });
  return definition;
}

export function buildFilterDefinition(
  simpleFilters: SimpleFilter[],
  customFilters: Filter[]
) {
  let definition = '';
  definition = buildSimpleFilterDefinition(definition, simpleFilters);
  definition = buildCustomFilterDefinition(definition, customFilters);

  return definition;
}

interface FilterProviderProps {
  children: ReactNode;
}

export const FilterProvider = ({ children }: FilterProviderProps) => {
  const [simpleFilters, setSimpleFilters] = useState<SimpleFilter[]>([]);
  const [customFilters, setCustomFilters] = useState<Filter[]>([]);
  const { filterOptions } = useFilterOptions();

  const { isPlaying } = useReplayContext();
  const { mutate: setFilter } = useSetFilters(isPlaying);

  const { toast } = useToast();

  const applyFilter = useCallback(
    (showToast: boolean): Promise<boolean> => {
      return new Promise((resolve) => {
        const filterDefinition = buildFilterDefinition(
          simpleFilters,
          customFilters
        );

        setFilter(filterDefinition, {
          onSuccess: () => {
            if (showToast && filterDefinition) {
              toast({
                className: 'apply-filter-success-toast',
                title: 'Success!',
                description: 'Filter applied successfully.',
              });
            }
            resolve(true);
          },
          onError: (error) => {
            if (showToast) {
              toast({
                className: 'apply-filter-error-toast',
                title: 'Error!',
                description: 'Failed to apply filter.',
                variant: 'destructive',
              });
            }
            resolve(false);
          },
        });
      });
    },
    [customFilters, setFilter, simpleFilters, toast]
  );

  const contextValue = useMemo(
    () => ({
      simpleFilters,
      setSimpleFilters,
      customFilters,
      setCustomFilters,
      applyFilter,
      filterOptions,
    }),
    [simpleFilters, customFilters, applyFilter, filterOptions]
  );

  return (
    <FilterContext.Provider value={contextValue}>
      {children}
    </FilterContext.Provider>
  );
};

export const useFilterContext = () => {
  const context = useContext(FilterContext);
  if (!context) {
    throw new Error(
      'useFilterContext must be used within a FilterContextProvider'
    );
  }
  return context;
};
